import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Fonts from "../../ui/global/Fonts";
import Colors from "../../ui/global/Colors";
import { Auth } from '@supabase/auth-ui-react'
import { ThemeSupa } from '@supabase/auth-ui-shared'
import { SupabaseClient } from '@supabase/supabase-js'
import { useLocation, useNavigate } from 'react-router-dom'
import LoadingSpinner from "../../ui/components/LoadingSpinner";
import Logo from "../../ui/components/Logo"; // Import Logo component

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100svh;
  width: 100%;
  background-color: ${Colors.black};
  padding: 10px;
`;

const Container = styled.div`
  width: 100%;
  max-width: 400px;
  margin: 40px auto;
  padding: 20px;
  background-color: ${Colors.white};
  border-radius: 8px;
  border: 1px solid ${Colors.grey1};
  font-family: ${Fonts.lexendMedium.fontFamily};
  font-weight: ${Fonts.lexendMedium.fontWeight};
  font-size: 16px;
`;

const Header = styled.h4`
  color: ${Colors.black};
  font-family: ${Fonts.lexendSemiBold.fontFamily};
  font-weight: ${Fonts.lexendSemiBold.fontWeight};
  font-size: 24px;
  text-align: center;
  margin: 10px 0 20px 0;
`;

const ErrorMessage = styled.p`
  color: ${Colors.red};
  margin-bottom: 20px;
  font-size: 18px;
`;

const SuccessMessage = styled.p`
  color: ${Colors.green};
  margin-bottom: 20px;
  font-size: 18px;
  text-align: center;
`;

interface ResetPasswordProps {
  supabase: SupabaseClient;
}

const ResetPassword = ({ supabase }: ResetPasswordProps) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Initialize auth state
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setLoading(false);
      } catch (err: any) {
        console.error('Error initializing auth:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    initializeAuth();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'USER_UPDATED') {
        // After password is successfully reset, redirect to root
        navigate('/', { replace: true });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase, navigate]);

  const renderAuth = () => (
    <PageWrapper>
      <Logo colorMode="dark" size={36}/> {/* Add Logo component here */}
      <Container>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <Header>Reset Password</Header>
        <Auth
          supabaseClient={supabase}
          view="update_password"
          providers={[]}
          theme="default"
          redirectTo={window.location.origin}
          showLinks={false}
          appearance={{
            theme: ThemeSupa,
            variables: {
              default: {
                colors: {
                  brand: Colors.grey9,
                  brandAccent: Colors.grey8,
                },
              },
            },
          }}
          localization={{
            variables: {
              update_password: {
                password_label: "New Password",
                password_input_placeholder: "Your new password",
                button_label: "Update Password",
                confirmation_text: "Your password has been updated!",
              },
            }
          }}
        />
      </Container>
    </PageWrapper>
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return renderAuth();
};

export default ResetPassword;